import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000';

const api = axios.create({
  baseURL: `${API_BASE_URL}/api/v1`,
  headers: { 'Content-Type': 'application/json' },
});

api.interceptors.request.use((config) => {
  const storageType = localStorage.getItem('auth_storage_type');
  const storage = storageType === 'session' ? sessionStorage : localStorage;
  const token = storage.getItem('access_token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

// ── Types ─────────────────────────────────────────────────────────────────────

export interface QuickFitClubOptions {
  drivers: { brand: string; model: string; lofts: number[] }[];
  irons: { brand: string; model: string }[];
}

export interface QuickFitPlayerForm {
  id: string;
  name: string;
  handicap: string;
  age: string;
  gender: 'Male' | 'Female';
  swing_tempo: '' | 'smooth' | 'moderate' | 'aggressive';
  swing_speed_driver: string;
  swing_speed_7iron: string;
  attack_angle_driver: number;         // degrees, positive = hit up
  attack_angle_irons: number;          // degrees, negative = downward
  launch_angle_driver: string;         // '' = unknown, degrees
  launch_angle_7i: string;             // '' = unknown, degrees
  dispersion_std_dev_driver: string;   // '' = unknown
  dispersion_std_dev_7i: string;       // '' = unknown
  dispersion_pattern_driver: string;   // '' | 'fade' | 'straight' | 'draw'
  spin_rate_driver: string;            // '' = unknown, RPM
  spin_rate_7i: string;                // '' = unknown, RPM
  driver_carry_distance: string;       // '' = unknown, yards
  carry_distance_7i: string;           // '' = unknown, yards
  carry_std_dev_driver: string;        // '' = unknown, yards
  carry_std_dev_7i: string;            // '' = unknown, yards
  ball_speed_std_dev_driver: string;   // '' = unknown, mph
  ball_speed_std_dev_7i: string;       // '' = unknown, mph
  launch_angle_std_dev_driver: string; // '' = unknown, degrees
  launch_angle_std_dev_7i: string;     // '' = unknown, degrees
  spin_rate_std_dev_driver: string;    // '' = unknown, RPM
  spin_rate_std_dev_7i: string;        // '' = unknown, RPM
  face_angle_std_dev_driver: string;   // '' = unknown, degrees
  face_angle_std_dev_7i: string;       // '' = unknown, degrees
  club_path_std_dev_driver: string;    // '' = unknown, degrees
  club_path_std_dev_7i: string;        // '' = unknown, degrees
  face_to_path_driver: string;         // '' = unknown, degrees
  smash_factor_driver: string;         // '' = handicap-derived fallback
  smash_factor_7i: string;             // '' = speed/handicap-derived fallback
  driver_brand: string;                // '' = don't know
  driver_model: string;
  driver_loft: string;           // '' = don't know
  driver_static_loft: string;    // '' = use selected loft if known
  driver_shaft_flex: string;     // '' = don't know
  driver_shaft_model: string;
  driver_shaft_weight: string;
  iron_brand: string;
  iron_model: string;
  iron_type: string;
  iron_shaft_flex: string;       // '' = don't know
  iron_shaft_model: string;
  iron_shaft_weight: string;
  wedge_lofts: number[];
}

export interface QuickFitPlayerResult {
  playerName: string;
  profileId: number;
  modelMode: QuickFitModelMode;
  driverResults: any | null;
  ironResults: any | null;
  wedgeResults: any | null;
  error: string | null;
}

export type QuickFitModelMode = 'v9' | 'rule_based';

// ── Helpers ───────────────────────────────────────────────────────────────────

function smashFactorFromHandicap(hcp: number): number {
  if (hcp <= 5) return 1.50;
  if (hcp <= 12) return 1.47;
  if (hcp <= 20) return 1.43;
  return 1.40;
}

function dispersionFromHandicap(hcp: number): number {
  if (hcp <= 5) return 8;
  if (hcp <= 12) return 13;
  if (hcp <= 20) return 20;
  return 27;
}

function ironSmashFactorFromHandicap(hcp: number): number {
  if (hcp <= 5) return 1.40;
  if (hcp <= 12) return 1.38;
  if (hcp <= 20) return 1.35;
  return 1.33;
}

function addNumericField(
  payload: Record<string, any>,
  field: string,
  value: string,
  parser: (raw: string) => number = parseFloat
) {
  if (value !== '') payload[field] = parser(value);
}

function buildProfilePayload(player: QuickFitPlayerForm): Record<string, any> {
  const hcp = parseInt(player.handicap, 10) || 15;
  const payload: Record<string, any> = {
    handicap: hcp,
    age: parseInt(player.age, 10) || 35,
    gender: player.gender,
    ...(player.swing_tempo ? { swing_tempo: player.swing_tempo } : {}),
    swing_speed_driver: parseInt(player.swing_speed_driver, 10) || 93,
    swing_speed_7iron: parseInt(player.swing_speed_7iron, 10) || 78,
    attack_angle_driver: player.attack_angle_driver,
    attack_angle_irons: player.attack_angle_irons,
    smash_factor_driver: player.smash_factor_driver !== ''
      ? parseFloat(player.smash_factor_driver)
      : smashFactorFromHandicap(hcp),
    smash_factor_7i: player.smash_factor_7i !== ''
      ? parseFloat(player.smash_factor_7i)
      : ironSmashFactorFromHandicap(hcp),
    dispersion_std_dev_7i: player.dispersion_std_dev_7i !== ''
      ? parseFloat(player.dispersion_std_dev_7i)
      : dispersionFromHandicap(hcp),
  };

  addNumericField(payload, 'launch_angle_driver', player.launch_angle_driver);
  addNumericField(payload, 'launch_angle_7i', player.launch_angle_7i);
  addNumericField(payload, 'dispersion_std_dev_driver', player.dispersion_std_dev_driver);
  if (player.dispersion_pattern_driver !== '') {
    payload.dispersion_pattern_driver = player.dispersion_pattern_driver;
  }
  addNumericField(payload, 'spin_rate_driver', player.spin_rate_driver, raw => parseInt(raw, 10));
  addNumericField(payload, 'spin_rate_7i', player.spin_rate_7i, raw => parseInt(raw, 10));
  addNumericField(payload, 'driver_carry_distance', player.driver_carry_distance, raw => parseInt(raw, 10));
  addNumericField(payload, 'carry_distance_7i', player.carry_distance_7i, raw => parseInt(raw, 10));
  addNumericField(payload, 'carry_std_dev_driver', player.carry_std_dev_driver);
  addNumericField(payload, 'carry_std_dev_7i', player.carry_std_dev_7i);
  addNumericField(payload, 'ball_speed_std_dev_driver', player.ball_speed_std_dev_driver);
  addNumericField(payload, 'ball_speed_std_dev_7i', player.ball_speed_std_dev_7i);
  addNumericField(payload, 'launch_angle_std_dev_driver', player.launch_angle_std_dev_driver);
  addNumericField(payload, 'launch_angle_std_dev_7i', player.launch_angle_std_dev_7i);
  addNumericField(payload, 'spin_rate_std_dev_driver', player.spin_rate_std_dev_driver, raw => parseInt(raw, 10));
  addNumericField(payload, 'spin_rate_std_dev_7i', player.spin_rate_std_dev_7i, raw => parseInt(raw, 10));
  addNumericField(payload, 'face_angle_std_dev_driver', player.face_angle_std_dev_driver);
  addNumericField(payload, 'face_angle_std_dev_7i', player.face_angle_std_dev_7i);
  addNumericField(payload, 'club_path_std_dev_driver', player.club_path_std_dev_driver);
  addNumericField(payload, 'club_path_std_dev_7i', player.club_path_std_dev_7i);
  addNumericField(payload, 'face_to_path_driver', player.face_to_path_driver);
  addNumericField(payload, 'current_driver_static_loft', player.driver_static_loft || player.driver_loft);

  payload.has_launch_monitor_driver = Boolean(
    player.launch_angle_driver || player.spin_rate_driver || player.driver_carry_distance ||
    player.ball_speed_std_dev_driver || player.face_angle_std_dev_driver
  );
  payload.has_launch_monitor_iron = Boolean(
    player.launch_angle_7i || player.spin_rate_7i || player.carry_distance_7i ||
    player.ball_speed_std_dev_7i || player.face_angle_std_dev_7i
  );

  // Build equipment_history if any current club data provided
  const driverData: Record<string, any> = {};
  if (player.driver_brand) driverData.brand = player.driver_brand;
  if (player.driver_model) driverData.model = player.driver_model;
  if (player.driver_loft) driverData.loft = parseFloat(player.driver_loft);
  if (player.driver_shaft_flex) driverData.shaft_flex = player.driver_shaft_flex;
  if (player.driver_shaft_model) driverData.shaft_model = player.driver_shaft_model;
  if (player.driver_shaft_weight) driverData.shaft_weight = parseInt(player.driver_shaft_weight, 10);

  const ironData: Record<string, any> = {};
  if (player.iron_brand) ironData.brand = player.iron_brand;
  if (player.iron_model) ironData.model = player.iron_model;
  if (player.iron_shaft_flex) ironData.shaft_flex = player.iron_shaft_flex;
  if (player.iron_shaft_model) ironData.shaft_model = player.iron_shaft_model;
  if (player.iron_shaft_weight) ironData.shaft_weight = parseInt(player.iron_shaft_weight, 10);
  if (player.iron_type) payload.current_iron_type = player.iron_type;

  const wedgeData: Record<string, any> = {};
  if (player.wedge_lofts.length > 0) wedgeData.lofts = player.wedge_lofts.join(',');

  const hasEquipment =
    Object.keys(driverData).length > 0 ||
    Object.keys(ironData).length > 0 ||
    Object.keys(wedgeData).length > 0;

  if (hasEquipment) {
    payload.equipment_history = {};
    if (Object.keys(driverData).length > 0) payload.equipment_history.current_clubs_driver = driverData;
    if (Object.keys(ironData).length > 0) payload.equipment_history.current_clubs_irons = ironData;
    if (Object.keys(wedgeData).length > 0) payload.equipment_history.current_clubs_wedges = wedgeData;
  }

  return payload;
}

// ── API calls ─────────────────────────────────────────────────────────────────

export async function getQuickFitOptions(): Promise<QuickFitClubOptions> {
  const response = await api.get('/equipment/quick-fit-options');
  return response.data;
}

async function processOnePlayer(player: QuickFitPlayerForm): Promise<QuickFitPlayerResult> {
  try {
    const profilePayload = buildProfilePayload(player);
    const profileResponse = await api.post('/quick-fit-multiple/profile/', profilePayload);
    const profileId: number = profileResponse.data.profile_id;

    const [driverResults, ironResults, wedgeResults] = await Promise.allSettled([
      api.post(`/ml/recommendations/drivers/rule-based?profile_id=${profileId}&profile_source=quick_fit_multiple`),
      api.post(`/ml/recommendations/irons/rule-based?profile_id=${profileId}&profile_source=quick_fit_multiple`),
      api.post(`/ml/recommendations/wedges/rule-based?profile_id=${profileId}&profile_source=quick_fit_multiple`),
    ]);

    return {
      playerName: player.name || `Player ${player.id}`,
      profileId,
      modelMode: 'rule_based',
      driverResults: driverResults.status === 'fulfilled' ? driverResults.value.data : null,
      ironResults: ironResults.status === 'fulfilled' ? ironResults.value.data : null,
      wedgeResults: wedgeResults.status === 'fulfilled' ? wedgeResults.value.data : null,
      error: null,
    };
  } catch (err: any) {
    return {
      playerName: player.name || `Player ${player.id}`,
      profileId: 0,
      modelMode: 'rule_based',
      driverResults: null,
      ironResults: null,
      wedgeResults: null,
      error: (() => {
        const detail = err.response?.data?.detail;
        if (Array.isArray(detail)) return detail.map((d: any) => d.msg || JSON.stringify(d)).join('; ');
        if (typeof detail === 'string') return detail;
        return err.message || 'Unknown error';
      })(),
    };
  }
}

async function processOnePlayerV9(player: QuickFitPlayerForm): Promise<QuickFitPlayerResult> {
  try {
    const profilePayload = buildProfilePayload(player);
    const profileResponse = await api.post('/quick-fit-multiple/profile/', profilePayload);
    const profileId: number = profileResponse.data.profile_id;

    const [driverResults, ironResults, wedgeResults] = await Promise.allSettled([
      api.post(`/ml/recommendations/drivers/v9?profile_id=${profileId}&profile_source=quick_fit_multiple`),
      api.post(`/ml/recommendations/irons/v9?profile_id=${profileId}&profile_source=quick_fit_multiple`),
      api.post(`/ml/recommendations/wedges/rule-based?profile_id=${profileId}&profile_source=quick_fit_multiple`),
    ]);

    return {
      playerName: player.name || `Player ${player.id}`,
      profileId,
      modelMode: 'v9',
      driverResults: driverResults.status === 'fulfilled' ? driverResults.value.data : null,
      ironResults: ironResults.status === 'fulfilled' ? ironResults.value.data : null,
      wedgeResults: wedgeResults.status === 'fulfilled' ? wedgeResults.value.data : null,
      error: null,
    };
  } catch (err: any) {
    return {
      playerName: player.name || `Player ${player.id}`,
      profileId: 0,
      modelMode: 'v9',
      driverResults: null,
      ironResults: null,
      wedgeResults: null,
      error: (() => {
        const detail = err.response?.data?.detail;
        if (Array.isArray(detail)) return detail.map((d: any) => d.msg || JSON.stringify(d)).join('; ');
        if (typeof detail === 'string') return detail;
        return err.message || 'Unknown error';
      })(),
    };
  }
}

export async function submitQuickFit(
  players: QuickFitPlayerForm[],
  modelMode: QuickFitModelMode = 'v9'
): Promise<QuickFitPlayerResult[]> {
  const processor = modelMode === 'v9' ? processOnePlayerV9 : processOnePlayer;
  return Promise.all(players.map(processor));
}

export function createEmptyPlayer(id: string, index: number): QuickFitPlayerForm {
  return {
    id,
    name: `Player ${index + 1}`,
    handicap: '',
    age: '',
    gender: 'Male',
    swing_tempo: '',
    swing_speed_driver: '',
    swing_speed_7iron: '',
    attack_angle_driver: 0,
    attack_angle_irons: -3,
    launch_angle_driver: '',
    launch_angle_7i: '',
    dispersion_std_dev_driver: '',
    dispersion_std_dev_7i: '',
    dispersion_pattern_driver: '',
    spin_rate_driver: '',
    spin_rate_7i: '',
    driver_carry_distance: '',
    carry_distance_7i: '',
    carry_std_dev_driver: '',
    carry_std_dev_7i: '',
    ball_speed_std_dev_driver: '',
    ball_speed_std_dev_7i: '',
    launch_angle_std_dev_driver: '',
    launch_angle_std_dev_7i: '',
    spin_rate_std_dev_driver: '',
    spin_rate_std_dev_7i: '',
    face_angle_std_dev_driver: '',
    face_angle_std_dev_7i: '',
    club_path_std_dev_driver: '',
    club_path_std_dev_7i: '',
    face_to_path_driver: '',
    smash_factor_driver: '',
    smash_factor_7i: '',
    driver_brand: '',
    driver_model: '',
    driver_loft: '',
    driver_static_loft: '',
    driver_shaft_flex: '',
    driver_shaft_model: '',
    driver_shaft_weight: '',
    iron_brand: '',
    iron_model: '',
    iron_type: '',
    iron_shaft_flex: '',
    iron_shaft_model: '',
    iron_shaft_weight: '',
    wedge_lofts: [],
  };
}
